import React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { accountingPeriodLocalesToTry } from '../../utils/formatDate';
import { getDatePickerProviderProps } from '../../utils/datePickerLocale';

export default function AccountingPeriodPicker({
  control,
  name,
  label,
  errors,
  readOnly = false,
  disabled = false,
  minDate = null,
  maxDate = null,
}) {
  const errorMessage = get(errors, name, {})?.message;
  const { language } = useTranslatedMessages();
  const datePickerLocale = getDatePickerProviderProps(language);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} {...datePickerLocale}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          // Convert string format "January 2024" to dayjs object
          const parseValue = (value) => {
            if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
            if (typeof value === 'string') {
              // Try English, Arabic, and Simplified Chinese month-year strings.
              const localesToTry = accountingPeriodLocalesToTry(value);
              for (const loc of localesToTry) {
                const parsed = dayjs(value, 'MMMM YYYY', loc, true);
                if (parsed.isValid()) return parsed;
              }
              return null;
            }
            return null;
          };

          // Format dayjs to "January 2024" string
          const formatValue = (date) => {
            if (!date) return null;
            if (!dayjs.isDayjs(date)) return null;
            return date.format('MMMM YYYY');
          };

          const dateValue = parseValue(field.value);

          let minDateValue = undefined;
          if (minDate) {
            const dayjsMinDate = dayjs.isDayjs(minDate) ? minDate : dayjs(minDate);
            if (dayjsMinDate.isValid()) {
              minDateValue = dayjsMinDate.startOf('month');
            }
          }

          let maxDateValue = undefined;
          if (maxDate) {
            const dayjsMaxDate = dayjs.isDayjs(maxDate) ? maxDate : dayjs(maxDate);
            if (dayjsMaxDate.isValid()) {
              maxDateValue = dayjsMaxDate.startOf('month');
            }
          }

          return (
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              label={label}
              value={dateValue}
              minDate={minDateValue}
              maxDate={maxDateValue}
              onChange={(newValue) => {
                // Store as formatted string "January 2024"
                if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
                  const formatted = formatValue(newValue);
                  field.onChange(formatted || '');
                } else {
                  field.onChange('');
                }
              }}
              readOnly={readOnly}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errorMessage,
                  helperText: errorMessage,
                  disabled: disabled,
                },
              }}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
}
